import React, { useState, useEffect } from 'react';
import { getFieldMappings, saveFieldMappings } from '../services/api';

// Default CRM field mappings based on standard Zoho CRM Contacts module field API names
const DEFAULT_EQUIFAX_MAPPINGS = {
	'forename': 'First_Name',
	'surname': 'Last_Name',
	'dateOfBirth': 'Date_of_Birth',
	'title': 'Salutation',
	'middleName': 'Middle_Name',
	'currentAddress.street': 'Mailing_Street',
	'currentAddress.postTown': 'Mailing_City',
	'currentAddress.postCode': 'Mailing_Zip',
};

const DEFAULT_KOUNT_MAPPINGS = {
	'forename': 'First_Name',
	'surname': 'Last_Name',
	'email': 'Email',
	'mobileNumber': 'Mobile',
	'clientReference': 'id',
	'addresses.line1': 'Mailing_Street',
	'addresses.line2': '',
	'addresses.postalCode': 'Mailing_Zip',
};

const DEFAULT_EQUIFAX_LEADS_MAPPINGS = {
	'forename': 'First_Name',
	'surname': 'Last_Name',
	'dateOfBirth': 'Date_of_Birth',
	'title': 'Salutation',
	'middleName': 'Middle_Name',
	'currentAddress.street': 'Street',
	'currentAddress.postTown': 'City',
	'currentAddress.postCode': 'Zip_Code',
};

const DEFAULT_KOUNT_LEADS_MAPPINGS = {
	'forename': 'First_Name',
	'surname': 'Last_Name',
	'email': 'Email',
	'mobileNumber': 'Mobile',
	'clientReference': 'id',
	'addresses.line1': 'Street',
	'addresses.line2': '',
	'addresses.postalCode': 'Zip_Code',
};

// Claims to CRM (Deals module) mapping
// equifaxField = source field from Equifax claim data (rentalAgreement)
// crmFieldApiName = CRM Deals module API field name (configurable)
const CLAIMS_CRM_FIELDS = [
	// Auto-calculated fields (disabled in UI)
	{ name: 'Contact_Name', required: true, source: 'auto', description: 'Auto: CRM Record ID' },
	{ name: 'Client_Reference_Number', required: true, source: 'auto', description: 'Auto: Client Reference' },
	{ name: 'Lender_Clean_Name', required: true, source: 'auto', description: 'Auto: companyName (cleaned)' },
	{ name: 'Stage', required: true, source: 'auto', description: 'Auto: from Lender CC_Process' },
	{ name: 'Lender', required: false, source: 'auto', description: 'Auto: from Lender lookup (picklist Name)' },
	{ name: 'Lender_New', required: false, source: 'auto', description: 'Auto: from Lender lookup (lookup ID)' },
	{ name: 'Deal_Name', required: true, source: 'auto', description: 'Auto: <Client_Reference>/PCP<iteration>' },
	{ name: 'Still_in_Contract', required: false, source: 'auto', description: 'Auto: Yes if no endDate or today is between start/end; No if endDate is in past' },
	// Equifax-mapped fields
	{ name: 'Agreement_Number', required: false, source: 'equifax', equifaxClaimField: 'accountNumber', description: 'Equifax claim field' },
	{ name: 'Contract_Start_Date', required: false, source: 'equifax', equifaxClaimField: 'startDate', description: 'Equifax claim field' },
	{ name: 'Contract_End_Date', required: false, source: 'equifax', equifaxClaimField: 'endDate', description: 'Equifax claim field' },
	{ name: 'Actual_Contract_Term', required: false, source: 'equifax', equifaxClaimField: 'fixedPaymentTerms.numberOfPayments', description: 'Equifax claim field' },
	{ name: 'Loan_Account_Type', required: false, source: 'equifax', equifaxClaimField: 'accountType', description: 'Equifax claim field' },
	{ name: 'Cash_Price', required: false, source: 'equifax', equifaxClaimField: 'startBalance.balanceAmount.amount', description: 'Equifax claim field' },
	{ name: 'Amount_of_Credit', required: false, source: 'equifax', equifaxClaimField: 'creditLimit.limit.amount', description: 'Equifax claim field' },
	{ name: 'Loan_Balance', required: false, source: 'equifax', equifaxClaimField: 'currentBalance.balanceAmount.amount', description: 'Equifax claim field' },
	{ name: 'Monthly_Payment', required: false, source: 'equifax', equifaxClaimField: 'fixedPaymentTerms.paymentAmount.amount', description: 'Equifax claim field' },
	{ name: 'Total_Amount_Payable', required: false, source: 'equifax', equifaxClaimField: 'defaultBalance.balanceAmount.amount', description: 'Equifax claim field' },
	{ name: 'Interest_Rate', required: false, source: 'equifax', equifaxClaimField: '', description: 'Equifax claim field (if available)' },
	{ name: 'Payment_Frequency', required: false, source: 'equifax', equifaxClaimField: 'paymentFrequency', description: 'Equifax claim field' },
	// Static fields
	{ name: 'Lead_Source', required: false, source: 'auto', description: 'Auto: from Client record Lead_Source' },
	{ name: 'Pipeline', required: true, source: 'static', staticValue: 'Onboarding', description: 'Static value' },
];

const DEFAULT_CLAIMS_CRM_MAPPINGS = {};
CLAIMS_CRM_FIELDS.forEach(f => {
	DEFAULT_CLAIMS_CRM_MAPPINGS[f.name] = {
		crmField: f.name,
		equifaxClaimField: f.equifaxClaimField || '',
		source: f.source,
		staticValue: f.staticValue || '',
	};
});

// Client Update mapping (post-Equifax update to Contact record)
const CLIENT_UPDATE_FIELDS = [
	{ name: 'Total_Claims', required: true, source: 'auto', description: 'Auto: Count of Deals tied to Contact' },
	{ name: 'Claims_Found', required: true, source: 'auto', description: 'Auto: "Valid Claims Located" or "No Valid Claims Located"' },
	{ name: 'CC_Script_Last_Run', required: true, source: 'auto', description: 'Auto: Current datetime' },
	{ name: 'Equifax_Credit_Check_Success', required: true, source: 'auto', description: 'Auto: true' },
	{ name: 'Previous_Name_s', required: false, source: 'auto', description: 'Auto: Consolidated previous names (comma-separated)' },
	{ name: 'Clients_Previous_Address', required: false, source: 'auto', description: 'Auto: Previous addresses subform' },
];

const DEFAULT_CLIENT_UPDATE_MAPPINGS = {};
CLIENT_UPDATE_FIELDS.forEach(f => {
	DEFAULT_CLIENT_UPDATE_MAPPINGS[f.name] = { crmField: f.name, source: 'auto' };
});

// Address Subform field mapping (for Clients_Previous_Address subform rows)
const ADDRESS_SUBFORM_FIELDS = [
	{ name: 'PA_County', required: false, description: 'County' },
	{ name: 'PA_District', required: false, description: 'District' },
	{ name: 'PA_Name', required: false, description: 'House/Building Name' },
	{ name: 'PA_Number', required: false, description: 'House Number' },
	{ name: 'PA_Post_Code', required: false, description: 'Post Code' },
	{ name: 'PA_Post_Town', required: false, description: 'Post Town' },
	{ name: 'PA_Street_1', required: false, description: 'Street Line 1' },
	{ name: 'PA_Street_2', required: false, description: 'Street Line 2' },
	{ name: 'PA_Sub_Building', required: false, description: 'Sub Building (Flat)' },
	{ name: 'Title', required: false, description: 'Title' },
];

const DEFAULT_ADDRESS_SUBFORM_MAPPINGS = {};
ADDRESS_SUBFORM_FIELDS.forEach(f => {
	DEFAULT_ADDRESS_SUBFORM_MAPPINGS[f.name] = { crmField: f.name, addressField: f.name };
});

const EQUIFAX_FIELDS = [
	{ name: 'forename', required: true },
	{ name: 'surname', required: true },
	{ name: 'dateOfBirth', required: true },
	{ name: 'title', required: false },
	{ name: 'middleName', required: false },
	{ name: 'clientReference', required: false },
	{ name: 'currentAddress.addressId', required: false },
	{ name: 'currentAddress.flat', required: false },
	{ name: 'currentAddress.houseName', required: false },
	{ name: 'currentAddress.houseNumber', required: false },
	{ name: 'currentAddress.street', required: true },
	{ name: 'currentAddress.street2', required: false },
	{ name: 'currentAddress.postTown', required: true },
	{ name: 'currentAddress.postCode', required: true },
	{ name: 'previousAddress.addressId', required: false },
	{ name: 'previousAddress.flat', required: false },
	{ name: 'previousAddress.houseName', required: false },
	{ name: 'previousAddress.houseNumber', required: false },
	{ name: 'previousAddress.street', required: false },
	{ name: 'previousAddress.street2', required: false },
	{ name: 'previousAddress.postTown', required: false },
	{ name: 'previousAddress.postCode', required: false },
	{ name: 'previousPreviousAddress.addressId', required: false },
	{ name: 'previousPreviousAddress.flat', required: false },
	{ name: 'previousPreviousAddress.houseName', required: false },
	{ name: 'previousPreviousAddress.houseNumber', required: false },
	{ name: 'previousPreviousAddress.street', required: false },
	{ name: 'previousPreviousAddress.street2', required: false },
	{ name: 'previousPreviousAddress.postTown', required: false },
	{ name: 'previousPreviousAddress.postCode', required: false },
	{ name: 'includeJsonReport', required: false },
	{ name: 'includePdfReport', required: false },
];

const KOUNT_NAO_FIELDS = [
	{ name: 'forename', required: true },
	{ name: 'surname', required: true },
	{ name: 'email', required: true },
	{ name: 'mobileNumber', required: true },
	{ name: 'clientReference', required: true },
	{ name: 'addresses.line1', required: true },
	{ name: 'addresses.line2', required: false },
	{ name: 'addresses.postalCode', required: true },
];

export default function FieldMappingEditor() {
	const [activeTab, setActiveTab] = useState('equifax');
	const [equifaxMappings, setEquifaxMappings] = useState({});
	const [kountMappings, setKountMappings] = useState({});
	const [equifaxLeadsMappings, setEquifaxLeadsMappings] = useState({});
	const [kountLeadsMappings, setKountLeadsMappings] = useState({});
	const [claimsCrmMappings, setClaimsCrmMappings] = useState({});
	const [clientUpdateMappings, setClientUpdateMappings] = useState({});
	const [addressSubformMappings, setAddressSubformMappings] = useState({});
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [message, setMessage] = useState(null);

	useEffect(() => {
		loadMappings();
	}, []);

	async function loadMappings() {
		try {
			const [eqData, kountData, claimsData, clientUpdateData, addressSubformData, eqLeadsData, kountLeadsData] = await Promise.all([
				getFieldMappings('equifax'),
				getFieldMappings('kount_nao'),
				getFieldMappings('claims_crm').catch(() => []),
				getFieldMappings('client_update').catch(() => []),
				getFieldMappings('client_address_subform').catch(() => []),
				getFieldMappings('equifax_leads').catch(() => []),
				getFieldMappings('kount_nao_leads').catch(() => []),
			]);

			const eqMap = {};
			for (const m of eqData) {
				eqMap[m.equifaxField] = m.crmFieldApiName;
			}
			if (eqData.length === 0) {
				Object.assign(eqMap, DEFAULT_EQUIFAX_MAPPINGS);
			}
			setEquifaxMappings(eqMap);

			const kountMap = {};
			for (const m of kountData) {
				kountMap[m.equifaxField] = m.crmFieldApiName;
			}
			if (kountData.length === 0) {
				Object.assign(kountMap, DEFAULT_KOUNT_MAPPINGS);
			}
			setKountMappings(kountMap);

			// Claims CRM mappings: stored as JSON in crmFieldApiName
			const claimsMap = {};
			for (const m of claimsData) {
				try {
					claimsMap[m.equifaxField] = JSON.parse(m.crmFieldApiName);
				} catch {
					claimsMap[m.equifaxField] = { crmField: m.crmFieldApiName, equifaxClaimField: '', source: 'equifax', staticValue: '' };
				}
			}
			if (claimsData.length === 0) {
				Object.assign(claimsMap, DEFAULT_CLAIMS_CRM_MAPPINGS);
			}
			setClaimsCrmMappings(claimsMap);

			// Client Update mappings
			const clientMap = {};
			for (const m of clientUpdateData) {
				try { clientMap[m.equifaxField] = JSON.parse(m.crmFieldApiName); }
				catch { clientMap[m.equifaxField] = { crmField: m.crmFieldApiName, source: 'auto' }; }
			}
			if (clientUpdateData.length === 0) Object.assign(clientMap, DEFAULT_CLIENT_UPDATE_MAPPINGS);
			setClientUpdateMappings(clientMap);

			// Address Subform mappings
			const addrMap = {};
			for (const m of addressSubformData) {
				try { addrMap[m.equifaxField] = JSON.parse(m.crmFieldApiName); }
				catch { addrMap[m.equifaxField] = { crmField: m.crmFieldApiName, addressField: m.equifaxField }; }
			}
			if (addressSubformData.length === 0) Object.assign(addrMap, DEFAULT_ADDRESS_SUBFORM_MAPPINGS);
			setAddressSubformMappings(addrMap);

			// Equifax Leads mappings
			const eqLeadsMap = {};
			for (const m of eqLeadsData) {
				eqLeadsMap[m.equifaxField] = m.crmFieldApiName;
			}
			if (eqLeadsData.length === 0) {
				Object.assign(eqLeadsMap, DEFAULT_EQUIFAX_LEADS_MAPPINGS);
			}
			setEquifaxLeadsMappings(eqLeadsMap);

			// Kount Leads mappings
			const kountLeadsMap = {};
			for (const m of kountLeadsData) {
				kountLeadsMap[m.equifaxField] = m.crmFieldApiName;
			}
			if (kountLeadsData.length === 0) {
				Object.assign(kountLeadsMap, DEFAULT_KOUNT_LEADS_MAPPINGS);
			}
			setKountLeadsMappings(kountLeadsMap);
		} catch (err) {
			setMessage({ type: 'error', text: 'Failed to load mappings: ' + err.message });
		} finally {
			setLoading(false);
		}
	}

	function handleAutoPopulate() {
		if (activeTab === 'equifax') {
			setEquifaxMappings(prev => {
				const updated = { ...prev };
				for (const [field, crmField] of Object.entries(DEFAULT_EQUIFAX_MAPPINGS)) {
					if (!updated[field]) updated[field] = crmField;
				}
				return updated;
			});
		} else if (activeTab === 'kount_nao') {
			setKountMappings(prev => {
				const updated = { ...prev };
				for (const [field, crmField] of Object.entries(DEFAULT_KOUNT_MAPPINGS)) {
					if (!updated[field]) updated[field] = crmField;
				}
				return updated;
			});
		} else if (activeTab === 'claims_crm') {
			setClaimsCrmMappings(prev => {
				const updated = { ...prev };
				for (const [field, mapping] of Object.entries(DEFAULT_CLAIMS_CRM_MAPPINGS)) {
					if (!updated[field]) updated[field] = { ...mapping };
				}
				return updated;
			});
		} else if (activeTab === 'client_update') {
			setClientUpdateMappings(prev => {
				const updated = { ...prev };
				for (const [field, mapping] of Object.entries(DEFAULT_CLIENT_UPDATE_MAPPINGS)) {
					if (!updated[field]) updated[field] = { ...mapping };
				}
				return updated;
			});
		} else if (activeTab === 'client_address_subform') {
			setAddressSubformMappings(prev => {
				const updated = { ...prev };
				for (const [field, mapping] of Object.entries(DEFAULT_ADDRESS_SUBFORM_MAPPINGS)) {
					if (!updated[field]) updated[field] = { ...mapping };
				}
				return updated;
			});
		}

		// Also auto-populate the Leads column when on equifax or kount tab
		if (activeTab === 'equifax') {
			setEquifaxLeadsMappings(prev => {
				const updated = { ...prev };
				for (const [field, crmField] of Object.entries(DEFAULT_EQUIFAX_LEADS_MAPPINGS)) {
					if (!updated[field]) updated[field] = crmField;
				}
				return updated;
			});
		} else if (activeTab === 'kount_nao') {
			setKountLeadsMappings(prev => {
				const updated = { ...prev };
				for (const [field, crmField] of Object.entries(DEFAULT_KOUNT_LEADS_MAPPINGS)) {
					if (!updated[field]) updated[field] = crmField;
				}
				return updated;
			});
		}

		setMessage({ type: 'success', text: 'Default mappings applied. Review and save when ready.' });
	}

	function handleChange(type, fieldName, value) {
		if (type === 'equifax') {
			setEquifaxMappings(prev => ({ ...prev, [fieldName]: value }));
		} else if (type === 'kount_nao') {
			setKountMappings(prev => ({ ...prev, [fieldName]: value }));
		} else if (type === 'equifax_leads') {
			setEquifaxLeadsMappings(prev => ({ ...prev, [fieldName]: value }));
		} else if (type === 'kount_nao_leads') {
			setKountLeadsMappings(prev => ({ ...prev, [fieldName]: value }));
		}
	}

	function handleClaimsCrmChange(fieldName, key, value) {
		setClaimsCrmMappings(prev => ({
			...prev,
			[fieldName]: { ...prev[fieldName], [key]: value }
		}));
	}

	async function handleSave() {
		setSaving(true);
		setMessage(null);
		try {
			if (activeTab === 'claims_crm') {
				const mappingsArray = CLAIMS_CRM_FIELDS.map(f => ({
					equifaxField: f.name,
					crmFieldApiName: JSON.stringify(claimsCrmMappings[f.name] || DEFAULT_CLAIMS_CRM_MAPPINGS[f.name]),
					isRequired: f.required
				}));
				await saveFieldMappings('claims_crm', mappingsArray);
				setMessage({ type: 'success', text: 'Claims to CRM mappings saved.' });
			} else if (activeTab === 'client_update') {
				const mappingsArray = CLIENT_UPDATE_FIELDS.map(f => ({
					equifaxField: f.name,
					crmFieldApiName: JSON.stringify(clientUpdateMappings[f.name] || DEFAULT_CLIENT_UPDATE_MAPPINGS[f.name]),
					isRequired: f.required
				}));
				await saveFieldMappings('client_update', mappingsArray);
				setMessage({ type: 'success', text: 'Client Update mappings saved.' });
			} else if (activeTab === 'client_address_subform') {
				const mappingsArray = ADDRESS_SUBFORM_FIELDS.map(f => ({
					equifaxField: f.name,
					crmFieldApiName: JSON.stringify(addressSubformMappings[f.name] || DEFAULT_ADDRESS_SUBFORM_MAPPINGS[f.name]),
					isRequired: f.required
				}));
				await saveFieldMappings('client_address_subform', mappingsArray);
				setMessage({ type: 'success', text: 'Address Subform mappings saved.' });
			} else {
				const isEquifax = activeTab === 'equifax';
				const fields = isEquifax ? EQUIFAX_FIELDS : KOUNT_NAO_FIELDS;
				const contactsMappings = isEquifax ? equifaxMappings : kountMappings;
				const leadsMappings = isEquifax ? equifaxLeadsMappings : kountLeadsMappings;
				const contactsType = isEquifax ? 'equifax' : 'kount_nao';
				const leadsType = isEquifax ? 'equifax_leads' : 'kount_nao_leads';
				const label = isEquifax ? 'Equifax' : 'Kount NAO';

				// Save Contacts mappings
				const contactsArray = fields.map(f => ({
					equifaxField: f.name,
					crmFieldApiName: contactsMappings[f.name] || '',
					isRequired: f.required
				}));
				await saveFieldMappings(contactsType, contactsArray);

				// Save Leads mappings
				const leadsArray = fields.map(f => ({
					equifaxField: f.name,
					crmFieldApiName: leadsMappings[f.name] || '',
					isRequired: f.required
				}));
				await saveFieldMappings(leadsType, leadsArray);

				setMessage({ type: 'success', text: `${label} mappings saved (Contacts + Leads).` });
			}
		} catch (err) {
			setMessage({ type: 'error', text: 'Failed to save: ' + err.message });
		} finally {
			setSaving(false);
		}
	}

	if (loading) return <p>Loading field mappings...</p>;

	// Render standard mapping table for equifax/kount — with Contacts + Leads columns
	function renderStandardTable() {
		const isEquifax = activeTab === 'equifax';
		const fields = isEquifax ? EQUIFAX_FIELDS : KOUNT_NAO_FIELDS;
		const contactsMappings = isEquifax ? equifaxMappings : kountMappings;
		const leadsMappings = isEquifax ? equifaxLeadsMappings : kountLeadsMappings;
		const contactsType = isEquifax ? 'equifax' : 'kount_nao';
		const leadsType = isEquifax ? 'equifax_leads' : 'kount_nao_leads';

		return (
			<table className="mapping-table">
				<thead>
					<tr>
						<th>API Field</th>
						<th>Required</th>
						<th>Contacts CRM Field</th>
						<th>Leads CRM Field</th>
					</tr>
				</thead>
				<tbody>
					{fields.map(field => (
						<tr key={field.name}>
							<td className="field-name">{field.name}</td>
							<td className="field-required">
								{field.required ? <span className="required-badge">Required</span> : 'Optional'}
							</td>
							<td>
								<input
									type="text"
									value={contactsMappings[field.name] || ''}
									onChange={(e) => handleChange(contactsType, field.name, e.target.value)}
									placeholder="e.g. First_Name"
								/>
							</td>
							<td>
								<input
									type="text"
									value={leadsMappings[field.name] || ''}
									onChange={(e) => handleChange(leadsType, field.name, e.target.value)}
									placeholder="e.g. First_Name"
								/>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		);
	}

	// Render claims-to-CRM mapping table
	function renderClaimsCrmTable() {
		return (
			<>
				<p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
					Map Equifax claim data to CRM Deals module fields. Auto fields are calculated automatically.
					For Equifax source fields, specify the claim property (e.g. accountNumber, endDate, startDate).
				</p>
				<table className="mapping-table">
					<thead>
						<tr>
							<th>CRM Deal Field</th>
							<th>Required</th>
							<th>Source</th>
							<th>Equifax Claim Field / Value</th>
							<th>CRM API Field Name</th>
						</tr>
					</thead>
					<tbody>
						{CLAIMS_CRM_FIELDS.map(field => {
							const mapping = claimsCrmMappings[field.name] || DEFAULT_CLAIMS_CRM_MAPPINGS[field.name];
							const isAuto = field.source === 'auto';
							const isStatic = field.source === 'static';

							return (
								<tr key={field.name} style={isAuto ? { backgroundColor: '#f0f7ff' } : {}}>
									<td className="field-name">
										{field.name}
										{isAuto && <span style={{ display: 'block', fontSize: '0.75rem', color: '#888' }}>{field.description}</span>}
									</td>
									<td className="field-required">
										{field.required ? <span className="required-badge">Required</span> : 'Optional'}
									</td>
									<td>
										<span style={{
											padding: '2px 8px',
											borderRadius: '4px',
											fontSize: '0.75rem',
											fontWeight: 'bold',
											backgroundColor: isAuto ? '#e3f2fd' : isStatic ? '#fff3e0' : '#e8f5e9',
											color: isAuto ? '#1565c0' : isStatic ? '#e65100' : '#2e7d32',
										}}>
											{isAuto ? 'AUTO' : isStatic ? 'STATIC' : 'EQUIFAX'}
										</span>
									</td>
									<td>
										{isAuto ? (
											<span style={{ color: '#888', fontStyle: 'italic' }}>{field.description}</span>
										) : isStatic ? (
											<input
												type="text"
												value={mapping.staticValue || ''}
												onChange={(e) => handleClaimsCrmChange(field.name, 'staticValue', e.target.value)}
												placeholder="Static value"
											/>
										) : (
											<input
												type="text"
												value={mapping.equifaxClaimField || ''}
												onChange={(e) => handleClaimsCrmChange(field.name, 'equifaxClaimField', e.target.value)}
												placeholder="e.g. accountNumber, endDate"
											/>
										)}
									</td>
									<td>
										<input
											type="text"
											value={mapping.crmField || ''}
											onChange={(e) => handleClaimsCrmChange(field.name, 'crmField', e.target.value)}
											placeholder={field.name}
										/>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</>
		);
	}

	function renderClientUpdateTable() {
		return (
			<>
				<p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
					Configure which CRM fields to update after a successful Equifax credit check. Change the CRM API Field Name to match your CRM's field names.
				</p>
				<table className="mapping-table">
					<thead>
						<tr>
							<th>Field</th>
							<th>Description</th>
							<th>CRM API Field Name</th>
						</tr>
					</thead>
					<tbody>
						{CLIENT_UPDATE_FIELDS.map(field => {
							const mapping = clientUpdateMappings[field.name] || DEFAULT_CLIENT_UPDATE_MAPPINGS[field.name];
							return (
								<tr key={field.name} style={{ backgroundColor: '#f0f7ff' }}>
									<td className="field-name">{field.name}</td>
									<td style={{ fontSize: '0.8rem', color: '#666' }}>{field.description}</td>
									<td>
										<input
											type="text"
											value={mapping.crmField || ''}
											onChange={(e) => setClientUpdateMappings(prev => ({
												...prev,
												[field.name]: { ...prev[field.name], crmField: e.target.value }
											}))}
											placeholder={field.name}
										/>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</>
		);
	}

	function renderAddressSubformTable() {
		return (
			<>
				<p style={{ fontSize: '0.85rem', color: '#666', marginBottom: '1rem' }}>
					Map the previous address subform fields. These are the CRM subform row fields used in the Clients_Previous_Address subform.
				</p>
				<table className="mapping-table">
					<thead>
						<tr>
							<th>Address Field</th>
							<th>Description</th>
							<th>CRM Subform Field Name</th>
						</tr>
					</thead>
					<tbody>
						{ADDRESS_SUBFORM_FIELDS.map(field => {
							const mapping = addressSubformMappings[field.name] || DEFAULT_ADDRESS_SUBFORM_MAPPINGS[field.name];
							return (
								<tr key={field.name}>
									<td className="field-name">{field.name}</td>
									<td style={{ fontSize: '0.8rem', color: '#666' }}>{field.description}</td>
									<td>
										<input
											type="text"
											value={mapping.crmField || ''}
											onChange={(e) => setAddressSubformMappings(prev => ({
												...prev,
												[field.name]: { ...prev[field.name], crmField: e.target.value }
											}))}
											placeholder={field.name}
										/>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</>
		);
	}

	function renderActiveTabContent() {
		switch (activeTab) {
			case 'claims_crm': return renderClaimsCrmTable();
			case 'client_update': return renderClientUpdateTable();
			case 'client_address_subform': return renderAddressSubformTable();
			default: return renderStandardTable();
		}
	}

	return (
		<div className="field-mapping-editor">
			<h3>Field Mapping</h3>
			<p>Map CRM field API names to the corresponding API fields.</p>

			{message && (
				<div className={`message ${message.type}`}>{message.text}</div>
			)}

			<div className="tabs">
				<button
					className={activeTab === 'equifax' ? 'tab active' : 'tab'}
					onClick={() => setActiveTab('equifax')}
				>
					Equifax Fields
				</button>
				<button
					className={activeTab === 'kount_nao' ? 'tab active' : 'tab'}
					onClick={() => setActiveTab('kount_nao')}
				>
					Kount NAO Fields
				</button>
				<button
					className={activeTab === 'claims_crm' ? 'tab active' : 'tab'}
					onClick={() => setActiveTab('claims_crm')}
				>
					Claims → CRM
				</button>
				<button
					className={activeTab === 'client_update' ? 'tab active' : 'tab'}
					onClick={() => setActiveTab('client_update')}
				>
					Client Update
				</button>
				<button
					className={activeTab === 'client_address_subform' ? 'tab active' : 'tab'}
					onClick={() => setActiveTab('client_address_subform')}
				>
					Address Subform
				</button>
			</div>

			{renderActiveTabContent()}

			<div className="action-bar">
				<button onClick={handleAutoPopulate} className="btn-secondary">
					Auto-populate Defaults
				</button>
				<button onClick={handleSave} disabled={saving} className="btn-primary">
					{saving ? 'Saving...' : 'Save Mappings'}
				</button>
			</div>
		</div>
	);
}
